/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import * as d3 from 'd3';
import {
  tempToKelvin,
  calculateThetaFromPressTemp,
  pressToPascal,
} from './bijvoetUtils';
import type { SoundingDiagramType } from '../../api/edr-api/types';
import { REFERENCE_PRESSURE } from '../Soundings/constants';
import type { ScalesType } from '../Soundings/types';

interface DrawIsothermsProps {
  staticPressureArray: number[];
  height: number;
  xScale: ScalesType;
  yScale: ScalesType;
  svg: d3.Selection<SVGElement, unknown, HTMLElement, unknown>;
  diagramType: SoundingDiagramType;
  d3GraphId: string;
}

export const drawIsotherms = ({
  staticPressureArray,
  height,
  xScale,
  yScale,
  svg,
  diagramType,
  d3GraphId,
}: DrawIsothermsProps): void => {
  //  lines of constant temperature (10 degree interval from -80...+40C)
  for (let temp = -80; temp <= 40; temp += 10) {
    const points: [number, number][] = [];

    for (const pressure of staticPressureArray) {
      const Ppx = yScale(pressure);
      const Tpx = xScale(temp);
      points.push([diagramType === 'SKEWT' ? Tpx + (height - Ppx) : Tpx, Ppx]);
    }

    const lineGenerator = d3.line();
    const pathString = lineGenerator(points);

    svg
      .append('path')
      .attr('d', pathString)
      .style('fill', 'none')
      .style('stroke-width', 1)
      .style('stroke', 'orange')
      .style('stroke-opacity', 0.5)
      .attr('clip-path', `url(#clip-${d3GraphId}-${diagramType}d3)`);
  }
};

interface DrawIsothermsBijvoetProps {
  staticPressureArray: number[];
  xScale: ScalesType;
  yScale: ScalesType;
  height: number;
  svg: d3.Selection<SVGElement, unknown, HTMLElement, unknown>;
  d3GraphId: string;
}
export const drawIsothermsBijvoet = ({
  staticPressureArray,
  xScale,
  yScale,
  svg,
  height,
  d3GraphId,
}: DrawIsothermsBijvoetProps): void => {
  // lines of constant temperature (2 degree interval from -150...+40C)
  for (let temp = -150; temp <= 40; temp += 2) {
    const points: [number, number][] = [];

    const Tiso = tempToKelvin(temp);

    for (const pressure of staticPressureArray) {
      const theta = calculateThetaFromPressTemp(
        pressToPascal(pressure),
        Tiso,
        REFERENCE_PRESSURE,
      );
      points.push([xScale(theta), yScale(pressure)]);
    }

    const lineGenerator = d3.line();
    const pathString = lineGenerator(points);
    const strID = `${temp}-isotherm-BIJVOET`;

    svg
      .append('path')
      .attr('id', strID)
      .attr('d', pathString)
      .style('fill', 'none')
      .style('stroke-width', temp % 10 === 0 ? 2 : 1)
      .style('stroke', temp === 0 ? '#D27D2D' : 'orange')
      .style('stroke-opacity', 0.5)
      .attr('clip-path', `url(#clip-${d3GraphId}-BIJVOETd3)`);

    if (temp % 10 === 0 && points[0]?.[1] === height) {
      svg
        .append('text')
        .text(temp)
        .attr('text-anchor', 'middle')
        .attr('x', `${points[0][0]}px`)
        .attr('y', `${height + 12}px`)
        .attr('fill', temp === 0 ? '#D27D2D' : 'orange')
        .attr('font-size', '10px');
    }
  }
};
